import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import styles from "../../styles/components/RichTextEditor.module.css";
import {
  replaceTemplateKeysWithPills,
  replacePillsWithTemplateKeys,
  normalizeTag,
} from "../../utils/templatesTag.js";

const Embed = Quill.import("blots/embed");

class TemplateTagBlot extends Embed {
  static create(value) {
    const tagValue = typeof value === "string" ? value : value?.value;
    const node = super.create();

    node.setAttribute("data-template-key", tagValue);
    node.setAttribute("contenteditable", "false");
    node.classList.add("template-tag-pill");

    const label = document.createElement("span");
    label.innerText = tagValue;
    label.classList.add("template-tag-label");

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.tabIndex = -1;
    removeButton.innerText = "×";
    removeButton.setAttribute("data-template-tag-remove", "true");
    removeButton.classList.add("template-tag-remove");

    node.appendChild(label);
    node.appendChild(removeButton);

    return node;
  }

  static value(node) {
    return node.getAttribute("data-template-key");
  }
}

TemplateTagBlot.blotName = "templateTag";
TemplateTagBlot.tagName = "span";
TemplateTagBlot.className = "template-tag-pill";

if (!Quill.imports["formats/templateTag"]) {
  Quill.register(TemplateTagBlot);
}

const toolbarOptions = [
  [{ font: [] }, { size: [] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ script: "sub" }, { script: "super" }],
  [{ header: 1 }, { header: 2 }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }],
  ["link", "image"],
  ["blockquote", "code-block"],
  ["clean"],
];

const WHATSAPP_TOOLBAR_OPTIONS = [["bold", "italic", "strike"], ["clean"]];

const MODULES_BY_TOOLBAR = {
  full: { toolbar: toolbarOptions },
  whatsapp: { toolbar: WHATSAPP_TOOLBAR_OPTIONS },
  none: { toolbar: false },
};

const formats = [
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "header",
  "list",
  "bullet",
  "align",
  "link",
  "image",
  "width",
  "height",
  "blockquote",
  "code-block",
  "templateTag",
];

const FORMATS_BY_TOOLBAR = {
  full: formats,
  whatsapp: ["bold", "italic", "strike", "templateTag"],
  none: ["templateTag"],
};

const MIN_IMAGE_WIDTH = 32;

const IMAGE_RESIZE_KEYBOARD_STEP = 10;

const IMAGE_RESIZE_HANDLES = [
  { position: "nw", horizontalSign: -1, label: "esquina superior izquierda" },
  { position: "ne", horizontalSign: 1, label: "esquina superior derecha" },
  { position: "sw", horizontalSign: -1, label: "esquina inferior izquierda" },
  { position: "se", horizontalSign: 1, label: "esquina inferior derecha" },
];

const RichTextEditor = ({
  label,
  value,
  onChange,
  placeholder = "Escribí acá...",
  minHeight = 240,
  readOnly = false,
  className = "",
  templateTags = [],
  toolbar = "full",
}) => {
  const quillRef = useRef(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const imageOverlayRef = useRef(null);
  const lastEmittedValueRef = useRef(null);
  const stopImageResizeRef = useRef(null);

  const [editorHtml, setEditorHtml] = useState(() => {
    return replaceTemplateKeysWithPills(value || "");
  });
  const [showTags, setShowTags] = useState(false);
  const [editorIsFocused, setEditorIsFocused] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageOverlayBox, setImageOverlayBox] = useState(null);

  const normalizedTags = useMemo(() => {
    return templateTags
      .map(normalizeTag)
      .filter((tag) => tag.label && tag.value);
  }, [templateTags]);

  const updateDropdownPosition = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    const wrapper = wrapperRef.current;

    if (!editor || !wrapper) return;

    const range = editor.getSelection();

    if (!range) return;

    const bounds = editor.getBounds(range.index);
    const editorRect = editor.root.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    setDropdownPosition({
      top: editorRect.top - wrapperRect.top + bounds.top - 2,
      left: editorRect.left - wrapperRect.left + bounds.left + 8,
    });
  }, []);

  const handleEditorChange = (content) => {
    const serializedValue = replacePillsWithTemplateKeys(content);

    setEditorHtml(content);
    lastEmittedValueRef.current = serializedValue;
    onChange?.(serializedValue);

    window.requestAnimationFrame(() => {
      updateDropdownPosition();
    });
  };

  const insertTag = (tagValue) => {
    const editor = quillRef.current?.getEditor();

    if (!editor) return;

    editor.focus();

    const range = editor.getSelection(true);
    const index = range?.index ?? editor.getLength();

    editor.insertEmbed(index, "templateTag", tagValue, "user");
    editor.insertText(index + 1, " ", "user");
    editor.setSelection(index + 2, 0, "user");

    setShowTags(false);

    window.requestAnimationFrame(() => {
      updateDropdownPosition();
    });
  };

  const removeTagElement = useCallback(
    (removeButton) => {
      const editor = quillRef.current?.getEditor();
      const pill = removeButton.closest("[data-template-key]");

      if (!editor || !pill) return;

      const blot = Quill.find(pill);

      if (!blot) return;

      const index = editor.getIndex(blot);

      editor.deleteText(index, 1, "user");
      editor.focus();

      window.requestAnimationFrame(() => {
        updateDropdownPosition();
      });
    },
    [updateDropdownPosition],
  );

  const measureSelectedImage = useCallback(() => {
    const wrapper = wrapperRef.current;

    if (!selectedImage || !wrapper || !selectedImage.isConnected) {
      setImageOverlayBox(null);
      return;
    }

    const imageRect = selectedImage.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    setImageOverlayBox({
      top: imageRect.top - wrapperRect.top,
      left: imageRect.left - wrapperRect.left,
      width: imageRect.width,
      height: imageRect.height,
    });
  }, [selectedImage]);

  const commitImageSize = useCallback((image) => {
    const editor = quillRef.current?.getEditor();
    const blot = Quill.find?.(image);

    if (!editor?.formatText || !blot) return;

    const index = editor.getIndex(blot);

    editor.formatText(
      index,
      1,
      {
        width: image.getAttribute("width") || false,
        height: image.getAttribute("height") || false,
      },
      "user",
    );
  }, []);

  const startImageResize = (event, horizontalSign) => {
    event.preventDefault();
    event.stopPropagation();

    const image = selectedImage;

    if (!image) return;

    const startRect = image.getBoundingClientRect();
    const aspectRatio = startRect.height / startRect.width || 1;
    const startClientX = event.clientX;
    const editorRoot = quillRef.current?.getEditor()?.root;
    const maxWidth = editorRoot?.clientWidth || startRect.width;

    const handleMouseMove = (moveEvent) => {
      const widthDelta = (moveEvent.clientX - startClientX) * horizontalSign;
      const nextWidth = Math.min(
        Math.max(startRect.width + widthDelta, MIN_IMAGE_WIDTH),
        Math.max(maxWidth, MIN_IMAGE_WIDTH),
      );

      image.setAttribute("width", String(Math.round(nextWidth)));
      image.setAttribute("height", String(Math.round(nextWidth * aspectRatio)));

      measureSelectedImage();
    };

    const stopImageResize = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopImageResize);
      window.removeEventListener("blur", stopImageResize);
      stopImageResizeRef.current = null;

      commitImageSize(image);
      measureSelectedImage();
    };

    stopImageResizeRef.current = stopImageResize;

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopImageResize);
    window.addEventListener("blur", stopImageResize);
  };

  const resizeImageByStep = (widthDelta) => {
    const image = selectedImage;

    if (!image) return;

    const currentRect = image.getBoundingClientRect();
    const aspectRatio = currentRect.height / currentRect.width || 1;
    const editorRoot = quillRef.current?.getEditor()?.root;
    const maxWidth = editorRoot?.clientWidth || currentRect.width;

    const nextWidth = Math.min(
      Math.max(currentRect.width + widthDelta, MIN_IMAGE_WIDTH),
      Math.max(maxWidth, MIN_IMAGE_WIDTH),
    );

    image.setAttribute("width", String(Math.round(nextWidth)));
    image.setAttribute("height", String(Math.round(nextWidth * aspectRatio)));

    commitImageSize(image);
    measureSelectedImage();
  };

  const handleImageResizeKeyDown = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();

    resizeImageByStep(
      event.key === "ArrowRight"
        ? IMAGE_RESIZE_KEYBOARD_STEP
        : -IMAGE_RESIZE_KEYBOARD_STEP,
    );
  };

  const resetImageSize = () => {
    if (!selectedImage) return;

    selectedImage.removeAttribute("width");
    selectedImage.removeAttribute("height");

    commitImageSize(selectedImage);

    window.requestAnimationFrame(() => {
      measureSelectedImage();
    });
  };

  useEffect(() => {
    return () => {
      stopImageResizeRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (value === lastEmittedValueRef.current) return;

    setSelectedImage(null);
    setEditorHtml(replaceTemplateKeysWithPills(value || ""));
  }, [value]);

  useEffect(() => {
    if (readOnly) setSelectedImage(null);
  }, [readOnly]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();

    if (!editor?.root) return undefined;

    const editorRoot = editor.root;

    const handleEditorClick = (event) => {
      if (readOnly) {
        setSelectedImage(null);
        return;
      }

      const clickedImage =
        event.target?.tagName === "IMG" ? event.target : null;

      setSelectedImage(clickedImage);
    };

    const handleDocumentMouseDown = (event) => {
      if (wrapperRef.current?.contains(event.target)) return;

      setSelectedImage(null);
    };

    editorRoot.addEventListener("click", handleEditorClick);
    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      editorRoot.removeEventListener("click", handleEditorClick);
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [readOnly]);

  useEffect(() => {
    if (!selectedImage) {
      setImageOverlayBox(null);
      return undefined;
    }

    measureSelectedImage();

    const editorRoot = quillRef.current?.getEditor()?.root;
    const handleReposition = () => measureSelectedImage();

    editorRoot?.addEventListener("scroll", handleReposition);
    window.addEventListener("resize", handleReposition);

    return () => {
      editorRoot?.removeEventListener("scroll", handleReposition);
      window.removeEventListener("resize", handleReposition);
    };
  }, [selectedImage, measureSelectedImage]);

  useEffect(() => {
    if (!imageOverlayRef.current || !imageOverlayBox) return;

    const overlayStyle = imageOverlayRef.current.style;

    overlayStyle.setProperty("--image-overlay-top", `${imageOverlayBox.top}px`);
    overlayStyle.setProperty(
      "--image-overlay-left",
      `${imageOverlayBox.left}px`,
    );
    overlayStyle.setProperty(
      "--image-overlay-width",
      `${imageOverlayBox.width}px`,
    );
    overlayStyle.setProperty(
      "--image-overlay-height",
      `${imageOverlayBox.height}px`,
    );
  }, [imageOverlayBox]);

  useEffect(() => {
    if (!wrapperRef.current) return;

    wrapperRef.current.style.setProperty(
      "--editor-min-height",
      `${minHeight}px`,
    );
  }, [minHeight]);

  useEffect(() => {
    if (!dropdownRef.current || !dropdownPosition) return;

    dropdownRef.current.style.setProperty(
      "--dropdown-top",
      `${dropdownPosition.top}px`,
    );
    dropdownRef.current.style.setProperty(
      "--dropdown-left",
      `${dropdownPosition.left}px`,
    );
  }, [dropdownPosition]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();

    if (!editor) return undefined;

    const handleMouseDown = (event) => {
      const removeButton = event.target.closest("[data-template-tag-remove]");

      if (!removeButton) return;

      event.preventDefault();
      event.stopPropagation();

      removeTagElement(removeButton);
    };

    editor.root.addEventListener("mousedown", handleMouseDown);

    return () => {
      editor.root.removeEventListener("mousedown", handleMouseDown);
    };
  }, [removeTagElement]);

  const shouldShowDropdownButton =
    !readOnly &&
    editorIsFocused &&
    dropdownPosition &&
    normalizedTags.length > 0;

  return (
    <div className={`${styles.editorWrapper} ${className}`}>
      {label ? <label className={styles.label}>{label}</label> : null}

      <div
        ref={wrapperRef}
        className={[
          styles.editorContainer,
          readOnly ? styles.editorContainerReadOnly : null,
          toolbar === "none" ? styles.editorContainerWithoutToolbar : null,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {shouldShowDropdownButton ? (
          <div ref={dropdownRef} className={styles.tagsDropdownWrapper}>
            <button
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                setShowTags((prev) => !prev);

                window.requestAnimationFrame(() => {
                  updateDropdownPosition();
                });
              }}
              className={styles.tagsDropdownButton}
            >
              +
            </button>

            {showTags ? (
              <div className={styles.tagsDropdownMenu}>
                {normalizedTags.map((tag) => (
                  <button
                    key={tag.value}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      insertTag(tag.value);
                    }}
                    className={styles.tagsDropdownItem}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={editorHtml}
          onChange={handleEditorChange}
          onFocus={() => {
            setEditorIsFocused(true);

            window.requestAnimationFrame(() => {
              updateDropdownPosition();
            });
          }}
          onBlur={() => {
            window.setTimeout(() => {
              setEditorIsFocused(false);
              setShowTags(false);
              setDropdownPosition(null);
            }, 100);
          }}
          onChangeSelection={(range) => {
            if (!range) return;

            setEditorIsFocused(true);

            window.requestAnimationFrame(() => {
              updateDropdownPosition();
            });
          }}
          placeholder={placeholder}
          readOnly={readOnly}
          modules={MODULES_BY_TOOLBAR[toolbar] ?? MODULES_BY_TOOLBAR.full}
          formats={FORMATS_BY_TOOLBAR[toolbar] ?? formats}
          className={styles.quillEditor}
        />

        {selectedImage && imageOverlayBox ? (
          <div
            ref={imageOverlayRef}
            className={styles.imageResizeOverlay}
            data-testid="image-resize-overlay"
          >
            {IMAGE_RESIZE_HANDLES.map((handle) => (
              <button
                key={handle.position}
                type="button"
                aria-label={`Redimensionar imagen desde la ${handle.label}`}
                data-testid={`image-resize-handle-${handle.position}`}
                className={`${styles.imageResizeHandle} ${
                  styles[`imageResizeHandle-${handle.position}`]
                }`}
                onMouseDown={(event) =>
                  startImageResize(event, handle.horizontalSign)
                }
                onKeyDown={handleImageResizeKeyDown}
              />
            ))}

            <div className={styles.imageResizeToolbar}>
              <span className={styles.imageResizeSize}>
                {`${Math.round(imageOverlayBox.width)} × ${Math.round(
                  imageOverlayBox.height,
                )} px`}
              </span>

              <button
                type="button"
                className={styles.imageResizeReset}
                onMouseDown={(event) => event.preventDefault()}
                onClick={resetImageSize}
              >
                Tamaño original
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RichTextEditor;
